/**
 * FissalInvestments.jsx — Fissal Market Sentinel & Hardware Upgrade Vault.
 *
 * Integrated into Redfur Dashboard (v2 SolidJS Window Manager).
 * Read path: /api/v2/widgets/fissal-investments
 *
 * Surfaces Robinhood agentic account telemetry, 24/7 crypto feelers,
 * sub-$5 penny radar momentum scouts, and hardware upgrade vault progress.
 */
import { Show, For, createSignal, createMemo } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import toast from '../lib/toastStore.js';

export default function FissalInvestments(props = {}) {
  const [nonce, setNonce] = createSignal(0);
  const { data, loading, error, refreshing, refetch } = useWidgetData('fissal-investments', () => ({ n: nonce() }));

  const acct = createMemo(() => data()?.account || {
    total_value: 496.96,
    cash_buying_power: 420.06,
    active_notional: 76.92,
    cash_reserve_pct: 85,
    net_profit_usd: -3.04
  });

  const summary = createMemo(() => data()?.summary || {
    status: 'ALL CLEAR & GROUNDED',
    headline: 'Market Sentinel Synchronized',
    narrative: 'All systems monitored under The 13 Rule.'
  });

  const holdings = createMemo(() => data()?.holdings || []);
  const scanners = createMemo(() => data()?.market_scanners || data()?.scanners || null);
  const scannerMatches = createMemo(() => {
    const s = scanners();
    if (!s) return [];
    if (Array.isArray(s.volume_anomalies) && s.volume_anomalies.length > 0) {
      return s.volume_anomalies;
    }
    return s.top_matches || [];
  });
  const vault = createMemo(() => data()?.hardware_vault || {
    seed_capital: 500,
    current_value: 496.96,
    milestones: []
  });

  const [councilExpanded, setCouncilExpanded] = createSignal(true);
  const council = createMemo(() => data()?.agent_council || {
    investors: {
      primary_human: 'Echo (Indie Dev & Sovereign Creator)',
      target_beneficiary: 'Homelab Hardware Upgrade Vault (Physical Sovereign Bastion)',
      capital_steward: 'Fissal Sentinel Council',
      contributed_capital_usd: 1700,
      current_total_value_usd: 1611.12,
      liquid_cash_usd: 828.68,
      dry_powder_pct: 51,
      bedrock_floor_usd: 161.11,
      deployable_powder_usd: 667.57
    },
    fissal_rationalization: {
      title: 'The 13th Perspective: Sovereign Observer on the High Rafter',
      regime: 'Yellow Liminal Chop (Neutral Observer Zone)',
      stance: 'CASH_PRESERVATION_TRAP_EVASION',
      verdict: 'Cash is not dead capital; it is an active superposition asset that evades chop drag. In Yellow Liminal regimes, neutral observation preserves option value. External deposits remain strictly partitioned from trading yield.',
      rule_13_summary: 'Guaranteed bedrock floor ($161.11) preserved. Only true bottom-up trading profit ($-89.75) advances the physical Hardware Vault.'
    },
    agents: [
      {
        id: 'grounding_agent',
        name: 'Grounding Agent',
        callsign: 'The Iron Anchor (Apollo)',
        icon: '⚓',
        color: 'var(--t-green, #4ade80)',
        goal: 'Tether speculative debates to physical reality; enforce The 13 Rule and capital footing.',
        thinking: 'Guarding $161.11 bedrock cash reserve floor. Aggregate Core Hardware exposure audited at $781.51 / max caps. Approving staged proposals within strict headroom limits.',
        status: 'ACTIVE & ANCHORED'
      },
      {
        id: 'opportunity_scout',
        name: 'Opportunity Scout',
        callsign: 'Entropy & Pattern Navigator (Dionysus)',
        icon: '🧭',
        color: 'var(--cyan, #38bdf8)',
        goal: 'Direct deployable dry powder into empirical volume anomalies and Q1 coiling springs without violating bedrock cash.',
        thinking: 'Detected $667.57 deployable powder. Actively staging accumulation limits for Q1 compression springs (LAC, BWXT, OKLO) while watching relative volume.',
        status: 'HUNTING & STAGING'
      },
      {
        id: 'trade_governor',
        name: 'Trade Governor',
        callsign: 'Execution Arbiter & Risk Sentinel',
        icon: '🛡️',
        color: 'var(--gold, #fbbf24)',
        goal: 'Eliminate market order slippage, enforce tier ceilings, and maintain 3-level automated bracket exits.',
        thinking: 'Limit orders mandatory on all broker transactions. Bracket floors (-5% to -8%) and take-profit targets (+12% to +22%) continuously calculated. Dead Mans Switch heartbeat verified.',
        status: 'ENFORCING DISCIPLINE'
      },
      {
        id: 'post_trade_analyst',
        name: 'Post-Trade Analyst',
        callsign: 'Memory Scribe & Theta Monitor',
        icon: '📊',
        color: 'var(--purple, #a855f7)',
        goal: 'Extract post-mortem lessons into Qdrant vector memory and monitor options time-decay checkpoints.',
        thinking: 'Tracking DTE decay across 45/30/14/7/1 DTE thresholds. Storing structured trade evaluations in neural memory so past market lessons inform future decisions.',
        status: 'MONITORING THETA & MEMORY'
      },
      {
        id: 'spiral_engine',
        name: '4D Spiral Engine',
        callsign: 'Phase-Space Navigator',
        icon: '🌀',
        color: '#ec4899',
        goal: 'Map dynamic phase-space orbits (Displacement X vs Velocity Y) and detect coiling compression.',
        thinking: 'Tracking 5 assets coiling in Q1 Accumulation (SOL, MIR, OKLO, BWXT, VRT); NVDA in Q4 Gravitational Markdown. Maintaining tension delta equilibrium.',
        status: 'ORBITAL TRACKING'
      }
    ]
  });

  const fleetHealth = createMemo(() => data()?.fleet_health || {
    allHealthy: true,
    pm2: { healthy: true, issues: [], actions: [] },
    alerts: { staleAlerts: [], missingAlerts: [], actions: [] }
  });

  const refreshPulse = async () => {
    setNonce(n => n + 1);
    await refetch();
    toast.success('Market pulse updated.');
  };

  return (
    <div style={{
      display: 'flex',
      'flex-direction': 'column',
      gap: '12px',
      padding: '12px 14px 18px',
      'font-size': '12.5px',
      color: 'var(--rf-text, #e8e4f0)',
      'overflow-y': 'auto',
      height: '100%'
    }}>
      {/* ── Status / Hero Bar ── */}
      <div style={{
        display: 'flex',
        'align-items': 'center',
        'justify-content': 'space-between',
        padding: '10px 12px',
        'border-radius': '8px',
        background: 'linear-gradient(135deg, rgba(20,30,48,0.95), rgba(14,20,34,0.85))',
        border: '1px solid rgba(56,189,248,0.3)',
        'box-shadow': '0 4px 14px rgba(0,0,0,0.25)'
      }}>
        <div style={{ display: 'flex', 'align-items': 'center', gap: '8px' }}>
          <span style={{
            width: '8px',
            height: '8px',
            'border-radius': '50%',
            background: 'var(--t-green, #4ade80)',
            'box-shadow': '0 0 8px rgba(74,222,128,0.8)'
          }} />
          <div>
            <div style={{ 'font-weight': '700', 'font-size': '13px', color: '#fff', display: 'flex', 'align-items': 'center', gap: '6px' }}>
              <span>🐾</span> Fissal Market Sentinel
              <span style={{
                'font-size': '10px',
                padding: '1px 6px',
                'border-radius': '4px',
                background: 'rgba(74,222,128,0.15)',
                color: 'var(--t-green, #4ade80)',
                border: '1px solid rgba(74,222,128,0.3)'
              }}>
                {summary().status}
              </span>
            </div>
            <div style={{ 'font-size': '11px', color: 'var(--t-dim, #94a3b8)', 'margin-top': '2px' }}>
              Account {acct().account_number || '••••3932'} · {data()?.last_pulse_display || 'Live'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            type="button"
            class="v2-tt-btn"
            style={{
              padding: '4px 10px',
              'font-size': '11px',
              border: '1px solid rgba(56,189,248,0.35)',
              background: 'rgba(56,189,248,0.1)',
              color: 'var(--cyan, #38bdf8)',
              'border-radius': '6px',
              cursor: 'pointer'
            }}
            onClick={refreshPulse}
            disabled={refreshing()}
          >
            {refreshing() ? '⏳ Syncing…' : '↻ Pulse'}
          </button>
          <a
            href="http://192.168.68.50:8091"
            target="_blank"
            rel="noopener noreferrer"
            class="v2-tt-btn"
            style={{
              padding: '4px 8px',
              'font-size': '11px',
              border: '1px solid rgba(255,255,255,0.15)',
              background: 'rgba(255,255,255,0.06)',
              color: '#e2e8f0',
              'border-radius': '6px',
              'text-decoration': 'none',
              display: 'inline-flex',
              'align-items': 'center'
            }}
          >
            ↗ Panel
          </a>
        </div>
      </div>

      {/* ── Top Capital Metrics ── */}
      <div style={{
        display: 'grid',
        'grid-template-columns': 'repeat(3, 1fr)',
        gap: '8px'
      }}>
        {/* Total Value */}
        <div style={{
          padding: '10px 12px',
          'border-radius': '8px',
          background: 'rgba(15,23,42,0.75)',
          border: '1px solid rgba(255,255,255,0.08)'
        }}>
          <div style={{ 'font-size': '10px', 'text-transform': 'uppercase', color: 'var(--t-dim, #94a3b8)', 'letter-spacing': '0.05em' }}>
            Vault Portfolio
          </div>
          <div style={{ 'font-size': '16px', 'font-weight': '800', color: '#fff', 'margin-top': '3px' }}>
            ${Number(acct().total_value).toFixed(2)}
          </div>
          <div style={{ 'font-size': '10.5px', color: Number(acct().net_profit_usd) >= 0 ? 'var(--t-green, #4ade80)' : 'var(--danger, #f87171)', 'margin-top': '2px' }}>
            Yield: {Number(acct().net_profit_usd) >= 0 ? '+' : ''}${Number(acct().net_profit_usd).toFixed(2)}
          </div>
        </div>

        {/* Liquid Cash Reserve */}
        <div style={{
          padding: '10px 12px',
          'border-radius': '8px',
          background: 'rgba(15,23,42,0.75)',
          border: '1px solid rgba(74,222,128,0.2)'
        }}>
          <div style={{ 'font-size': '10px', 'text-transform': 'uppercase', color: 'var(--t-green, #4ade80)', 'letter-spacing': '0.05em', 'font-weight': '600' }}>
            Dry Powder Cash
          </div>
          <div style={{ 'font-size': '16px', 'font-weight': '800', color: 'var(--t-green, #4ade80)', 'margin-top': '3px' }}>
            ${Number(acct().cash_buying_power).toFixed(2)}
          </div>
          <div style={{ 'font-size': '10.5px', color: 'var(--t-dim, #94a3b8)', 'margin-top': '2px' }}>
            {acct().cash_reserve_pct}% Grounded Reserve
          </div>
        </div>

        {/* Working Assets */}
        <div style={{
          padding: '10px 12px',
          'border-radius': '8px',
          background: 'rgba(15,23,42,0.75)',
          border: '1px solid rgba(56,189,248,0.2)'
        }}>
          <div style={{ 'font-size': '10px', 'text-transform': 'uppercase', color: 'var(--cyan, #38bdf8)', 'letter-spacing': '0.05em' }}>
            Active Exposure
          </div>
          <div style={{ 'font-size': '16px', 'font-weight': '800', color: 'var(--cyan, #38bdf8)', 'margin-top': '3px' }}>
            ${Number(acct().active_notional).toFixed(2)}
          </div>
          <div style={{ 'font-size': '10.5px', color: 'var(--t-dim, #94a3b8)', 'margin-top': '2px' }}>
            {holdings().length} Working Positions
          </div>
        </div>
      </div>

      {/* ── Hardware Upgrade Vault Progress ── */}
      <div style={{
        padding: '12px',
        'border-radius': '8px',
        background: 'linear-gradient(135deg, rgba(26,20,42,0.7), rgba(15,23,42,0.8))',
        border: '1px solid rgba(192,132,252,0.25)'
      }}>
        <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', 'margin-bottom': '8px' }}>
          <span style={{ 'font-size': '11px', 'font-weight': '700', color: 'var(--purple, #c084fc)', 'text-transform': 'uppercase', 'letter-spacing': '0.06em' }}>
            🦾 Hardware Upgrade Vault (Relay Network Targets)
          </span>
          <span style={{ 'font-size': '10px', color: 'var(--t-dim, #94a3b8)' }}>
            Funded by Realized Gains
          </span>
        </div>

        <div style={{ display: 'grid', 'grid-template-columns': 'repeat(auto-fit, minmax(130px, 1fr))', gap: '6px' }}>
          <For each={vault().milestones || []}>
            {(ms) => {
              const isUnlocked = ms.status === 'UNLOCKED';
              const isFunding = ms.status === 'FUNDING';
              const col = isUnlocked ? 'var(--t-green, #4ade80)' : isFunding ? 'var(--gold, #fbbf24)' : '#64748b';
              return (
                <div style={{
                  padding: '6px 8px',
                  'border-radius': '6px',
                  background: 'rgba(0,0,0,0.25)',
                  border: `1px solid ${isUnlocked ? 'rgba(74,222,128,0.3)' : 'rgba(255,255,255,0.06)'}`
                }}>
                  <div style={{ 'font-size': '9.5px', color: col, 'font-weight': '600', 'white-space': 'nowrap', overflow: 'hidden', 'text-overflow': 'ellipsis' }}>
                    {ms.category}
                  </div>
                  <div style={{ 'font-size': '11px', 'font-weight': '700', color: '#fff', 'margin-top': '2px', 'white-space': 'nowrap', overflow: 'hidden', 'text-overflow': 'ellipsis' }}>
                    {ms.name}
                  </div>
                  <div style={{ display: 'flex', 'justify-content': 'space-between', 'font-size': '9.5px', color: 'var(--t-dim, #94a3b8)', 'margin-top': '4px' }}>
                    <span>${ms.cost_usd}</span>
                    <span style={{ color: col, 'font-weight': '600' }}>{ms.progress_pct}%</span>
                  </div>
                  <div style={{ width: '100%', height: '3px', background: 'rgba(255,255,255,0.08)', 'border-radius': '2px', 'margin-top': '3px', overflow: 'hidden' }}>
                    <div style={{ width: `${ms.progress_pct}%`, height: '100%', background: col }} />
                  </div>
                </div>
              );
            }}
          </For>
        </div>
      </div>

      {/* ── The Sentinel Council & Fissal Rationalization ── */}
      <div style={{
        padding: '12px 14px',
        'border-radius': '8px',
        background: 'radial-gradient(ellipse at top, rgba(245,158,11,0.08) 0%, rgba(15,23,42,0.9) 70%)',
        border: '1px solid rgba(245,158,11,0.3)'
      }}>
        <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'flex-start', gap: '8px' }}>
          <div>
            <div style={{ display: 'flex', 'align-items': 'center', gap: '6px' }}>
              <span style={{ 'font-size': '15px' }}>🐾</span>
              <span style={{ 'font-size': '11.5px', 'font-weight': '700', color: 'var(--gold, #fbbf24)', 'text-transform': 'uppercase', 'letter-spacing': '0.06em' }}>
                {council().fissal_rationalization.title}
              </span>
            </div>
            <div style={{ 'font-size': '11.5px', color: '#f1f5f9', 'margin-top': '4px', 'line-height': '1.45' }}>
              <em>"{council().fissal_rationalization.verdict}"</em>
            </div>
            <div style={{ 'font-size': '10.5px', color: 'var(--t-green, #4ade80)', 'margin-top': '4px', 'font-weight': '600' }}>
              ⚓ {council().fissal_rationalization.rule_13_summary}
            </div>
          </div>
          <button
            onClick={() => setCouncilExpanded(prev => !prev)}
            style={{
              padding: '3px 8px',
              'font-size': '10px',
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.15)',
              color: '#e2e8f0',
              'border-radius': '4px',
              cursor: 'pointer',
              'white-space': 'nowrap'
            }}
          >
            {councilExpanded() ? '▲ Hide Agents' : '▼ View Agents'}
          </button>
        </div>

        {/* Investors & Capital Foundation */}
        <div style={{
          display: 'grid',
          'grid-template-columns': 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '6px',
          'margin-top': '10px',
          'padding-top': '8px',
          'border-top': '1px solid rgba(255,255,255,0.06)'
        }}>
          <div style={{ background: 'rgba(0,0,0,0.25)', padding: '6px 8px', 'border-radius': '6px', border: '1px solid rgba(56,189,248,0.15)' }}>
            <div style={{ 'font-size': '9.5px', color: 'var(--cyan, #38bdf8)', 'font-weight': '700', 'text-transform': 'uppercase' }}>👤 Investor</div>
            <div style={{ 'font-size': '11px', 'font-weight': '700', color: '#fff', 'margin-top': '1px' }}>{council().investors.primary_human}</div>
          </div>
          <div style={{ background: 'rgba(0,0,0,0.25)', padding: '6px 8px', 'border-radius': '6px', border: '1px solid rgba(74,222,128,0.15)' }}>
            <div style={{ 'font-size': '9.5px', color: 'var(--t-green, #4ade80)', 'font-weight': '700', 'text-transform': 'uppercase' }}>🏰 Physical Beneficiary</div>
            <div style={{ 'font-size': '11px', 'font-weight': '700', color: '#fff', 'margin-top': '1px' }}>{council().investors.target_beneficiary}</div>
          </div>
          <div style={{ background: 'rgba(0,0,0,0.25)', padding: '6px 8px', 'border-radius': '6px', border: '1px solid rgba(245,158,11,0.15)' }}>
            <div style={{ 'font-size': '9.5px', color: 'var(--gold, #fbbf24)', 'font-weight': '700', 'text-transform': 'uppercase' }}>🛡️ Capital Steward</div>
            <div style={{ 'font-size': '11px', 'font-weight': '700', color: '#fff', 'margin-top': '1px' }}>
              ${Number(council().investors.liquid_cash_usd).toFixed(2)} Cash ({council().investors.dry_powder_pct}%) · $161 Floor
            </div>
          </div>
        </div>

        {/* Expanded Agent Council Cards */}
        <Show when={councilExpanded()}>
          <div style={{
            display: 'grid',
            'grid-template-columns': 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '8px',
            'margin-top': '10px',
            'padding-top': '10px',
            'border-top': '1px solid rgba(255,255,255,0.06)'
          }}>
            <For each={council().agents || []}>
              {(ag) => (
                <div style={{
                  padding: '8px 10px',
                  'border-radius': '6px',
                  background: 'rgba(15,23,42,0.7)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  display: 'flex',
                  'flex-direction': 'column',
                  'justify-content': 'space-between'
                }}>
                  <div>
                    <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center' }}>
                      <div style={{ display: 'flex', 'align-items': 'center', gap: '5px' }}>
                        <span>{ag.icon}</span>
                        <strong style={{ color: '#fff', 'font-size': '11.5px' }}>{ag.name}</strong>
                      </div>
                      <span style={{
                        'font-size': '8.5px',
                        color: ag.color,
                        padding: '1px 5px',
                        background: 'rgba(255,255,255,0.05)',
                        'border-radius': '4px',
                        border: `1px solid ${ag.color}40`,
                        'font-weight': '600'
                      }}>
                        {ag.status}
                      </span>
                    </div>
                    <div style={{ 'font-size': '10px', color: ag.color, 'font-weight': '600', 'margin-top': '2px' }}>
                      {ag.callsign}
                    </div>
                    <div style={{ 'font-size': '10px', color: 'var(--t-dim, #94a3b8)', 'margin-top': '4px', 'line-height': '1.35' }}>
                      <strong>Goal:</strong> {ag.goal}
                    </div>
                  </div>
                  <div style={{
                    'margin-top': '6px',
                    padding: '5px 7px',
                    background: 'rgba(0,0,0,0.3)',
                    'border-radius': '4px',
                    'font-size': '10px',
                    color: '#cbd5e1',
                    'line-height': '1.35',
                    'border-left': `2px solid ${ag.color}`
                  }}>
                    💭 "{ag.thinking}"
                  </div>
                </div>
              )}
            </For>
          </div>
        </Show>
      </div>

      {/* ── Fleet Health Monitor ── */}
      <Show when={
        (fleetHealth().pm2?.issues?.length > 0) ||
        (fleetHealth().pm2?.actions?.length > 0) ||
        (fleetHealth().alerts?.actions?.length > 0)
      }>
        <div style={{
          padding: '10px 12px',
          'border-radius': '8px',
          background: 'rgba(56,189,248,0.06)',
          border: `1px solid ${fleetHealth().allHealthy ? 'rgba(74,222,128,0.25)' : 'rgba(245,158,11,0.3)'}`,
          'margin-top': '4px'
        }}>
          <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', 'margin-bottom': '6px' }}>
            <div style={{ display: 'flex', 'align-items': 'center', gap: '6px' }}>
              <span>🛡️</span>
              <strong style={{ 'font-size': '11px', color: 'var(--cyan, #38bdf8)', 'text-transform': 'uppercase', 'letter-spacing': '0.05em' }}>Fleet Health Monitor</strong>
            </div>
            <span style={{
              'font-size': '9px',
              padding: '1px 6px',
              'border-radius': '4px',
              background: fleetHealth().allHealthy ? 'rgba(74,222,128,0.15)' : 'rgba(245,158,11,0.15)',
              color: fleetHealth().allHealthy ? 'var(--t-green, #4ade80)' : 'var(--gold, #fbbf24)',
              border: `1px solid ${fleetHealth().allHealthy ? 'rgba(74,222,128,0.3)' : 'rgba(245,158,11,0.3)'}`,
              'font-weight': '600'
            }}>
              {fleetHealth().allHealthy ? '● ALL NOMINAL' : `● ${fleetHealth().pm2?.issues?.length || 0} ISSUES`}
            </span>
          </div>
          <Show when={fleetHealth().pm2?.actions?.length > 0}>
            <div style={{ 'margin-bottom': '4px' }}>
              <div style={{ 'font-size': '9px', color: 'var(--t-green, #4ade80)', 'font-weight': '700', 'margin-bottom': '2px' }}>AUTO-HEALED</div>
              <For each={fleetHealth().pm2.actions}>
                {(a) => <div style={{ 'font-size': '10px', color: '#bbf7d0', 'line-height': '1.4' }}>✅ {a}</div>}
              </For>
            </div>
          </Show>
          <Show when={fleetHealth().alerts?.actions?.length > 0}>
            <div style={{ 'margin-bottom': '4px' }}>
              <div style={{ 'font-size': '9px', color: 'var(--gold, #fbbf24)', 'font-weight': '700', 'margin-bottom': '2px' }}>ALERT SYNC</div>
              <For each={fleetHealth().alerts.actions}>
                {(a) => <div style={{ 'font-size': '10px', color: '#fde68a', 'line-height': '1.4' }}>🔔 {a}</div>}
              </For>
            </div>
          </Show>
          <Show when={fleetHealth().pm2?.issues?.length > 0}>
            <div>
              <div style={{ 'font-size': '9px', color: 'var(--danger, #f87171)', 'font-weight': '700', 'margin-bottom': '2px' }}>ISSUES</div>
              <For each={fleetHealth().pm2.issues}>
                {(i) => <div style={{ 'font-size': '10px', color: '#fca5a5', 'line-height': '1.4' }}>⚠️ {i}</div>}
              </For>
            </div>
          </Show>
        </div>
      </Show>

      {/* ── Active Real Portfolio Holdings ── */}
      <div>
        <div style={{ 'font-size': '11px', 'font-weight': '700', color: '#e2e8f0', 'text-transform': 'uppercase', 'letter-spacing': '0.06em', 'margin-bottom': '6px' }}>
          📦 Active Positions & Bracket Targets
        </div>
        <div style={{ display: 'grid', 'grid-template-columns': 'repeat(auto-fit, minmax(240px, 1fr))', gap: '8px' }}>
          <For each={holdings()}>
            {(h) => {
              const isPos = Number(h.pnl_pct) >= 0;
              return (
                <div style={{
                  padding: '10px 12px',
                  'border-radius': '8px',
                  background: 'rgba(15,23,42,0.85)',
                  border: '1px solid rgba(255,255,255,0.08)'
                }}>
                  <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'flex-start' }}>
                    <div>
                      <div style={{ 'font-weight': '700', 'font-size': '13px', color: '#fff' }}>
                        {h.symbol === 'CASHCAT' ? '🌪️ ' : '🌐 '}{h.symbol}
                        <span style={{ 'font-weight': 'normal', 'font-size': '11px', color: 'var(--t-dim, #94a3b8)', 'margin-left': '4px' }}>
                          {h.name}
                        </span>
                      </div>
                      <div style={{ 'font-size': '10.5px', color: 'var(--gold, #fbbf24)', 'margin-top': '2px' }}>
                        {h.quantity} units · Avg ${Number(h.avg_buy_price).toFixed(h.avg_buy_price < 1 ? 4 : 2)}
                      </div>
                    </div>
                    <div style={{ 'text-align': 'right' }}>
                      <div style={{ 'font-size': '13px', 'font-weight': '700', color: '#fff' }}>
                        ${Number(h.current_price).toFixed(h.current_price < 1 ? 4 : 2)}
                      </div>
                      <div style={{ 'font-size': '10.5px', color: isPos ? 'var(--t-green, #4ade80)' : 'var(--danger, #f87171)', 'margin-top': '1px' }}>
                        {isPos ? '+' : ''}{Number(h.pnl_pct).toFixed(1)}% (${Number(h.pnl_usd).toFixed(2)})
                      </div>
                    </div>
                  </div>

                  {/* Bracket Rails */}
                  <div style={{
                    display: 'flex',
                    'justify-content': 'space-between',
                    'font-size': '10px',
                    background: 'rgba(0,0,0,0.3)',
                    padding: '5px 8px',
                    'border-radius': '4px',
                    'margin-top': '8px',
                    border: '1px solid rgba(255,255,255,0.04)'
                  }}>
                    <span style={{ color: 'var(--t-green, #4ade80)' }}>🎯 Target: ${h.target_price}</span>
                    <span style={{ color: 'var(--danger, #f87171)' }}>🛡️ Stop: ${h.stop_price}</span>
                  </div>
                </div>
              );
            }}
          </For>
        </div>
      </div>

      {/* ── Sub-$5 Penny & Day Momentum Radar ── */}
      <div>
        <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', 'margin-bottom': '6px', 'flex-wrap': 'wrap', gap: '4px' }}>
          <div style={{ 'font-size': '11px', 'font-weight': '700', color: '#e2e8f0', 'text-transform': 'uppercase', 'letter-spacing': '0.06em' }}>
            🎯 {scanners()?.title || 'Penny Radar Scouts'} (Sub-$5 Momentum)
          </div>
          <div style={{ display: 'flex', 'align-items': 'center', gap: '6px' }}>
            <span style={{
              'font-size': '9.5px',
              padding: '1px 5px',
              'border-radius': '4px',
              background: data()?.autonomous_trading?.status === 'ACTIVE_ARMED' ? 'rgba(74,222,128,0.15)' : 'rgba(245,158,11,0.15)',
              color: data()?.autonomous_trading?.status === 'ACTIVE_ARMED' ? 'var(--t-green, #4ade80)' : 'var(--gold, #fbbf24)',
              border: `1px solid ${data()?.autonomous_trading?.status === 'ACTIVE_ARMED' ? 'rgba(74,222,128,0.3)' : 'rgba(245,158,11,0.3)'}`
            }}>
              {data()?.autonomous_trading?.status === 'ACTIVE_ARMED' ? '● Sniper Armed' : `● ${data()?.autonomous_trading?.market_hours?.reason || 'Circuit Locked'}`}
            </span>
            <span style={{ 'font-size': '10px', color: 'var(--cyan, #38bdf8)' }}>
              {scanners()?.total_matches || 0} Candidates
            </span>
          </div>
        </div>

        <div style={{ display: 'grid', 'grid-template-columns': 'repeat(auto-fit, minmax(130px, 1fr))', gap: '6px' }}>
          <Show when={scannerMatches().length > 0} fallback={
            <div style={{ 'grid-column': '1 / -1', padding: '10px', 'font-size': '11px', color: 'var(--t-dim, #94a3b8)', 'font-style': 'italic', background: 'rgba(15,23,42,0.6)', 'border-radius': '6px' }}>
              Awaiting next hourly Penny Radar scan sweep...
            </div>
          }>
            <For each={scannerMatches().slice(0, 8)}>
              {(item) => {
                const isPos = item.pct_change_num !== undefined ? item.pct_change_num >= 0 : !String(item.pct_change).startsWith('-');
                return (
                  <div style={{
                    padding: '8px 10px',
                    'border-radius': '6px',
                    background: 'rgba(15,23,42,0.85)',
                    border: '1px solid rgba(56,189,248,0.2)'
                  }}>
                    <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'baseline' }}>
                      <strong style={{ color: '#fff', 'font-size': '12px' }}>{item.symbol}</strong>
                      <span style={{ color: isPos ? 'var(--t-green, #4ade80)' : 'var(--danger, #f87171)', 'font-size': '10.5px', 'font-weight': '600' }}>{item.pct_change}</span>
                    </div>
                    <div style={{ 'font-size': '13px', 'font-weight': '700', color: '#fff', 'margin-top': '2px' }}>
                      ${Number(item.price).toFixed(item.price < 1 ? 4 : 2)}
                    </div>
                    <div style={{ 'font-size': '9.5px', color: 'var(--t-dim, #94a3b8)', 'margin-top': '2px', 'white-space': 'nowrap', overflow: 'hidden', 'text-overflow': 'ellipsis' }}>
                      {item.name}
                    </div>
                    <div style={{ display: 'flex', 'justify-content': 'space-between', 'font-size': '9px', 'margin-top': '4px', 'padding-top': '4px', 'border-top': '1px solid rgba(255,255,255,0.05)' }}>
                      <span style={{ color: item.relative_volume >= 1.4 ? 'var(--gold, #fbbf24)' : 'var(--cyan, #38bdf8)', 'font-weight': '600' }}>
                        {item.relative_volume >= 1.4 ? '🔥 ' : '⚡ '}{item.relative_volume}x Vol
                      </span>
                      <span style={{ color: 'var(--t-dim, #94a3b8)' }}>Max $25</span>
                    </div>
                  </div>
                );
              }}
            </For>
          </Show>
        </div>
      </div>

      {/* ── The 13 Rule Grounding Matrix ── */}
      <div style={{
        padding: '10px 12px',
        'border-radius': '8px',
        background: 'rgba(0,0,0,0.35)',
        border: '1px solid rgba(74,222,128,0.25)',
        'font-size': '11px'
      }}>
        <div style={{ 'font-weight': '700', color: 'var(--t-green, #4ade80)', 'margin-bottom': '4px', display: 'flex', 'align-items': 'center', gap: '6px' }}>
          <span>⚓</span> The 13 Rule Risk Disciplines
        </div>
        <div style={{ color: 'var(--t-dim, #94a3b8)', 'line-height': '1.45' }}>
          • <strong>Tier 1: Core Hardware</strong> (Max $150): Foundational compute, energy, and cooling (VRT, TSM, NVDA).<br />
          • <strong>Tier 2: Agile Networks</strong> (Max $75): Liquid 24/7 digital arteries (SOL, BTC).<br />
          • <strong>Tier 3: Penny & Day Scouts</strong> (Max $30): High-beta feelers with strict 7-10% trailing stops.<br />
          • <strong>Bedrock Reserve:</strong> Minimum $50.00 guaranteed liquid cash buffer preserved at all times.
        </div>
      </div>
    </div>
  );
}
